function registerInsideViewportListener(target, callback) {
  const rootMarginBottom = window.screen.width < 800 ? "-50px" : "-150px";
  const options = {
    root: null,
    rootMargin: `0px 0px ${rootMarginBottom} 0px`,
    threshold: 0,
  };

  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        callback();
      }
    });
  }, options);
  observer.observe(target);
}

function ensureValidEETValue(value) {
  if (!value || value.toString() === "") {
    return "n/a";
  }
  return value.toString();
}

function buildEETItem(
  productId,
  variantId,
  gtin,
  name,
  index,
  availability,
  stockLevel,
  brand,
  category,
  listId,
  listName,
  quantity,
  price,
  compareAtPrice
) {
  return {
    item_id:
      "shopify_US_" +
      ensureValidEETValue(productId) +
      "_" +
      ensureValidEETValue(variantId),
    item_gtin: ensureValidEETValue(gtin),
    item_name: ensureValidEETValue(name),
    discount: "n/a",
    compare_at_price: ensureValidEETValue(compareAtPrice),
    index: index || "n/a",
    item_availability: ensureValidEETValue(availability),
    item_stock_level: ensureValidEETValue(stockLevel),
    item_brand: ensureValidEETValue(brand),
    item_category: ensureValidEETValue(category),
    item_list_id: ensureValidEETValue(listId),
    item_list_name: ensureValidEETValue(listName),
    quantity: ensureValidEETValue(quantity),
    price: ensureValidEETValue(price),
  };
}

function pushEETDatalayer(eventName, ecommerceObj, avoidDuplicates = false) {
  const addToDatalayer = () => {
    window.dataLayer = window.dataLayer || [];

    if (avoidDuplicates) {
      const stringifiedEcommerceObj = JSON.stringify(ecommerceObj);
      const foundDuplicate = window.dataLayer.some((dataLayerEntry) => {
        return (
          JSON.stringify(dataLayerEntry.ecommerce) === stringifiedEcommerceObj
        );
      });

      if (foundDuplicate) {
        console.log(
          "[EET] Prevent pushing to datalayer. Found Duplicate for " +
            eventName,
          ecommerceObj
        );
        return;
      }
    }

    dataLayer.push({ ecommerce: null });
    dataLayer.push({
      event: eventName,
      ecommerce: ecommerceObj,
    });
    console.log("[EET] " + eventName, ecommerceObj);
  };

  if (window.customCmpConsentLoaded == true) {
    addToDatalayer();
  } else {
    console.log(
      "[EET] Waiting for customCmpConsentLoaded event for " + eventName,
      ecommerceObj
    );
    window.addEventListener("customCmpConsentLoaded", () => {
      addToDatalayer();
    });
  }
}

function getEETStorage() {
  const currentStorage = localStorage.getItem("eet_storage") || "[]";
  return JSON.parse(currentStorage);
}

function buildEETItemFromSelector(
  variantId,
  parentSelector = null,
  index = null,
  item_list_id = null,
  item_list_name = null
) {
  if (!Number.isInteger(variantId)) {
    variantId = parseInt(variantId);
  }
  const matchingEETVariantInfo = (parentSelector || document).querySelector(
    `[data-eet-variant-id='${variantId}']`
  );
  if (!matchingEETVariantInfo) {
    console.warn(
      "Could not find matching variant info element for " + variantId
    );
    return;
  }

  const variantJson = JSON.parse(matchingEETVariantInfo.textContent);

  if (index) {
    variantJson.index = index;
  }
  if (item_list_id && item_list_id !== "") {
    variantJson.item_list_id = item_list_id;
  }
  if (item_list_name && item_list_name !== "") {
    variantJson.item_list_name = item_list_name;
  }

  return variantJson;
}

function saveToEETStorage(variantId) {
  if (!Number.isInteger(variantId)) {
    variantId = parseInt(variantId);
  }

  let currentEETStorage = getEETStorage();
  const matchingEETVariantInfo = document.querySelector(
    `[data-eet-variant-id='${variantId}']`
  );
  if (!matchingEETVariantInfo) {
    console.warn(
      "Could not find matching variant info element for " + variantId
    );
    return;
  }

  currentEETStorage = currentEETStorage.filter((eetItem) => {
    return eetItem.id != variantId;
  });

  const variantJson = JSON.parse(matchingEETVariantInfo.textContent);
  currentEETStorage.push(variantJson);

  localStorage.setItem("eet_storage", JSON.stringify(currentEETStorage));
}

function removeFromEETStorage(variantId) {
  if (!Number.isInteger(variantId)) {
    variantId = parseInt(variantId);
  }

  let currentEETStorage = getEETStorage();

  currentEETStorage = currentEETStorage.filter((eetItem) => {
    return eetItem.variant_id != variantId;
  });

  localStorage.setItem("eet_storage", JSON.stringify(currentEETStorage));
}

function getFromEETStorage(variantId) {
  if (!Number.isInteger(variantId)) {
    variantId = parseInt(variantId);
  }

  let currentEETStorage = getEETStorage();

  const eetStorageItem = currentEETStorage.find(
    (eetItem) => eetItem.variant_id == variantId
  );

  if (!eetStorageItem) {
    console.warn("Could not find matching eetItem for variant_id " + variantId);
  }

  return eetStorageItem;
}

/*

    CART EVENTS

*/

async function pushViewCart(isCartDrawer = false) {
  const cartResponse = await fetch("/cart.js");
  if (!cartResponse.ok) {
    console.error("Could not fetch cart data in push view cart");
    return;
  }
  const cartData = await cartResponse.json();

  const eetItemList = [];

  for (let i = 0; i < cartData.items.length; i++) {
    const matchingCartItem = cartData.items[i];
    const eetItem = getFromEETStorage(matchingCartItem.variant_id);
    if (!eetItem) {
      continue;
    }
    eetItem.index = i + 1;
    eetItem.quantity = matchingCartItem.quantity;

    eetItemList.push(eetItem);
  }

  pushEETDatalayer("view_cart", {
    currency: "EUR",
    value: cartData.total_price / 100,
    cart_view_point: isCartDrawer ? "cart_overlay" : "cart_page",
    items: eetItemList,
  });
}

function pushRemoveFromCart(line, variantId, quantity, isCartDrawer = false) {
  const eetItem = getFromEETStorage(variantId);

  if (!eetItem) {
    return;
  }

  eetItem.index = parseInt(line) || 0;
  eetItem.quantity = quantity;

  pushEETDatalayer("remove_from_cart", {
    currency: "EUR",
    value: eetItem.price,
    cart_view_point: isCartDrawer ? "cart_overlay" : "cart_page",
    items: [eetItem],
  });
}

document.addEventListener("cart:item-added", function (evt) {
  saveToEETStorage(evt.detail?.product?.variant_id);
});

let lastPushViewCart = null;
document.addEventListener("cart-drawer:open", function (evt) {
  // time check because this event gets fired > 2 times in a row
  if (!lastPushViewCart || new Date().getTime() - lastPushViewCart > 1000) {
    pushViewCart(true);
    lastPushViewCart = new Date().getTime();
    return;
  }
});

if (window.location.pathname.startsWith("/cart")) {
  pushViewCart(false);
}

let lastLineItemChange = null;
document.addEventListener("line-item:change:start", function (evt) {
  const preventEvent =
    lastLineItemChange != null &&
    new Date().getTime() - lastLineItemChange < 500;
  lastLineItemChange = new Date().getTime();

  if (preventEvent) {
    return;
  }

  let matchingLineItemEl;
  let isCartDrawer;

  if (window.location.pathname.startsWith("/cart")) {
    matchingLineItemEl = document.querySelector(`#CartItem-${evt.detail.line}`);
    isCartDrawer = false;
  } else {
    matchingLineItemEl = document.querySelector(
      `#CartDrawerItem-${evt.detail.line}`
    );
    isCartDrawer = true;
  }

  const quantityDifference =
    evt.detail.quantity - parseInt(matchingLineItemEl.dataset.quantity);

  const matchingVariantId = matchingLineItemEl.dataset.variantId;

  if (quantityDifference < 0) {
    console.log(evt.detail, quantityDifference);
    pushRemoveFromCart(
      evt.detail.line,
      matchingVariantId,
      Math.abs(quantityDifference),
      isCartDrawer
    );
    if (evt.detail.quantity < 1) {
      removeFromEETStorage(matchingVariantId);
    }
    return;
  }
});

/*

    view_promotion and select_promotion events

*/

function pushViewPromotion(
  itemListId,
  itemListName,
  creativeName,
  creativeSlot,
  promotionId,
  promotionName,
  variantIds,
  parentSelector
) {
  const eetItemList = [];
  if (variantIds) {
    for (let i = 0; i < variantIds.length; i++) {
      const eetItem = buildEETItemFromSelector(
        variantIds[i],
        parentSelector,
        i + 1,
        itemListId,
        itemListName
      );
      eetItemList.push(eetItem);
    }
  }

  pushEETDatalayer(
    "view_promotion",
    {
      creative_name: creativeName,
      creative_slot: creativeSlot,
      promotion_id: promotionId,
      promotion_name: promotionName,
      items: eetItemList,
      item_list_id: itemListId,
      item_list_name: itemListName,
    },
    true
  );
}

function pushSelectPromotion(
  itemListId,
  itemListName,
  creativeName,
  creativeSlot,
  promotionId,
  promotionName,
  variantId,
  parentSelector,
  index
) {
  const eetItemList = [];
  if (variantId) {
    const eetItem = buildEETItemFromSelector(
      variantId,
      parentSelector,
      index,
      itemListId,
      itemListName
    );
    eetItemList.push(eetItem);
  }

  pushEETDatalayer("select_promotion", {
    creative_name: creativeName,
    creative_slot: creativeSlot,
    promotion_id: promotionId,
    promotion_name: promotionName,
    items: eetItemList,
    item_list_id: itemListId,
    item_list_name: itemListName,
  });
}

document.addEventListener("DOMContentLoaded", () => {
  document
    .querySelectorAll("[data-eet-view-promotion]")
    .forEach((eetViewPromotionEl) => {
      const creativeName = eetViewPromotionEl.dataset.creativeName;
      const creativeSlot = eetViewPromotionEl.dataset.creativeSlot;
      const promotionId = eetViewPromotionEl.dataset.promotionId;
      const promotionName = eetViewPromotionEl.dataset.promotionName;
      const itemListId = eetViewPromotionEl.dataset.itemListId;
      const itemListName = eetViewPromotionEl.dataset.itemListName;

      registerInsideViewportListener(eetViewPromotionEl, () => {
        const variantIds = Array.from(
          eetViewPromotionEl.querySelectorAll("[data-eet-variant-id]")
        ).map((el) => el.dataset.eetVariantId);
        pushViewPromotion(
          itemListId,
          itemListName,
          creativeName,
          creativeSlot,
          promotionId,
          promotionName,
          variantIds,
          eetViewPromotionEl
        );
      });

      const selectPromotionList = eetViewPromotionEl.querySelectorAll(
        "[data-eet-select-promotion]"
      );
      for (let i = 0; i < selectPromotionList.length; i++) {
        const eetSelectPromotionEl = selectPromotionList[i];
        const variantId = eetSelectPromotionEl.dataset.variantId;
        eetSelectPromotionEl.addEventListener("click", (e) => {
          if (!e.target.hasAttribute("data-prevent-eet-select-promotion")) {
            pushSelectPromotion(
              itemListId,
              itemListName,
              creativeName,
              creativeSlot,
              promotionId,
              promotionName,
              variantId,
              eetViewPromotionEl,
              i + 1
            );
          }
        });
      }
    });
});

/* 

    add_to_wishlist events

    DOM Implementation for category pages on select_item level

*/

function pushAddToWishlist(
  itemListId,
  itemListName,
  variantId,
  parentSelector,
  index
) {
  const eetItemList = [];
  const eetItem = buildEETItemFromSelector(
    variantId,
    parentSelector,
    index,
    itemListId,
    itemListName
  );
  eetItemList.push(eetItem);

  if (eetItemList.length == 0) {
    console.warn(
      "[EET] Prevent add_to_wishlist for itemListId " +
        itemListId +
        " because length of items is 0."
    );
    return;
  }

  pushEETDatalayer(
    "add_to_wishlist",
    {
      item_list_id: itemListId,
      item_list_name: itemListName,
      items: eetItemList,
    },
    true
  );
}

function eetWaitForElm(selector, parentSelector) {
  return new Promise((resolve) => {
    if (document.querySelector(selector)) {
      return resolve(document.querySelector(selector));
    }

    const observer = new MutationObserver((mutations) => {
      if (document.querySelector(selector)) {
        resolve(document.querySelector(selector));
        observer.disconnect();
      }
    });

    if (parentSelector != null) {
      observer.observe(document.querySelector(parentSelector), {
        childList: true,
        subtree: true,
      });
    }
  });
}

if (location.pathname.includes("/products/")) {
  document.addEventListener("DOMContentLoaded", () => {
    eetWaitForElm(".frcp-wishlist-btn", ".product-title-container").then(
      (wishlistButton) => {
        wishlistButton.addEventListener("click", () => {
          const isRemoveBtn = wishlistButton.classList.contains(
            "frcp-wishlist-btn--remove"
          );
          const variantId = document.querySelector(".wishlist-container")
            .dataset.variantId;
          if (isRemoveBtn) {
            console.log("[EET] Detected Remove Wishlist Btn, skipping.");
            return;
          }
          pushAddToWishlist("n/a", "n/a", variantId, null, 1);
        });
      }
    );
  });
}

/* 

    view_item_list and select_item events

*/

function pushViewItemList(
  itemListId,
  itemListName,
  variantIds,
  parentSelector
) {
  const eetItemList = [];
  if (variantIds) {
    for (let i = 0; i < variantIds.length; i++) {
      const eetItem = buildEETItemFromSelector(
        variantIds[i],
        parentSelector,
        i + 1,
        itemListId,
        itemListName
      );
      eetItemList.push(eetItem);
    }
  }

  if (eetItemList.length == 0) {
    console.warn(
      "[EET] Prevent view_item_list for itemListId " +
        itemListId +
        " because length of items is 0."
    );
    return;
  }

  pushEETDatalayer(
    "view_item_list",
    {
      item_list_id: itemListId,
      item_list_name: itemListName,
      items: eetItemList,
    },
    true
  );
}

function pushSelectItem(
  itemListId,
  itemListName,
  variantId,
  parentSelector,
  index
) {
  const eetItemList = [];
  if (variantId) {
    const eetItem = buildEETItemFromSelector(
      variantId,
      parentSelector,
      index,
      itemListId,
      itemListName
    );
    eetItemList.push(eetItem);
  }

  pushEETDatalayer("select_item", {
    item_list_id: itemListId,
    item_list_name: itemListName,
    items: eetItemList,
  });
}

function registerItemList(eetViewItemList) {
  const itemListId = eetViewItemList.dataset.itemListId;
  const itemListName = eetViewItemList.dataset.itemListName;

  registerInsideViewportListener(eetViewItemList, () => {
    const variantIds = Array.from(
      eetViewItemList.querySelectorAll("[data-eet-variant-id]")
    ).map((el) => el.dataset.eetVariantId);
    pushViewItemList(itemListId, itemListName, variantIds, eetViewItemList);
  });

  const selectItemList = eetViewItemList.querySelectorAll(
    "[data-eet-select-item]"
  );
  for (let i = 0; i < selectItemList.length; i++) {
    const eetSelectItemEl = selectItemList[i];
    const variantId = eetSelectItemEl.dataset.variantId;
    eetSelectItemEl.addEventListener("click", (e) => {
      if (e.target.hasAttribute("data-prevent-eet-select-item")) {
        return;
      }

      //Using parent classlist so that a click on the svg path element is captured as well
      const targetClassList = Array.from(e.target.classList).concat(
        Array.from(e.target.parentElement.classList)
      );
      const isWishlist = targetClassList.some((className) =>
        className.includes("frcp")
      );

      if (isWishlist) {
        // Pushing wishlist for items presented in a list (e.g. collections)
        const wishlistButtonEl = e.target.closest(".frcp-wishlist-btn");
        const isRemoveBtn = wishlistButtonEl.classList.contains(
          "frcp-wishlist-btn--remove"
        );
        if (isRemoveBtn) {
          console.log("[EET] Detected Remove Wishlist Btn, skipping.");
          return;
        }
        pushAddToWishlist(
          itemListId,
          itemListName,
          variantId,
          eetViewItemList,
          i + 1
        );
        return;
      }

      pushSelectItem(
        itemListId,
        itemListName,
        variantId,
        eetViewItemList,
        i + 1
      );
    });
  }
}

document.addEventListener("DOMContentLoaded", () => {
  document
    .querySelectorAll(
      "[data-eet-view-item-list]:not([data-prevent-eet-on-init]"
    )
    .forEach((eetViewItemList) => {
      registerItemList(eetViewItemList);
    });
});

function pushKennaEvent() {
  pushEETDatalayer("send_kenna_message", null, false);
}
